import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCog, Users, Plus, Pencil, Trash2, KeyRound, Sun, Moon, Monitor } from "lucide-react";
import api, { TOKEN_KEY } from "@/lib/api";
import { useAuth, apiErr } from "@/context/AuthContext";
import { useTheme } from "@/hooks/useTheme";
import { PageTitle, Panel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const ROLE_INFO = {
  OWNER: { label: "OWNER", desc: "Akses penuh termasuk kelola pengguna" },
  ACCOUNTING: { label: "ACCOUNTING", desc: "Semua fitur, kecuali tambah/edit/hapus pengguna" },
  ADMIN: { label: "ADMIN PRODUKSI", desc: "Input & edit transaksi, tanpa fungsi owner" },
  PENCATAT: { label: "PENCATAT", desc: "Operator modul jahitan (login pakai username)" },
  VIEWER: { label: "PENGAWAS", desc: "Hanya melihat" },
};

function RolePill({ role }) {
  const c = role === "OWNER" ? "bg-slate-900 text-white" : role === "ACCOUNTING" ? "bg-indigo-100 text-indigo-800" : role === "ADMIN" ? "bg-blue-100 text-blue-800" : role === "PENCATAT" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700";
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold ${c}`}>{ROLE_INFO[role]?.label || role}</span>;
}

function Profile() {
  const { user, setUser } = useAuth();
  const [theme, setTheme] = useTheme();
  const [f, setF] = useState({ name: user?.name || "", email: user?.email || "" });
  const [p, setP] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const save = async (payload, ok) => {
    setBusy(true);
    try {
      const { data } = await api.put("/auth/me", payload);
      localStorage.setItem(TOKEN_KEY, data.access_token); setUser(data.user); toast.success(ok);
      setP({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Panel title="Profil Saya" testId="account-profile">
        <div className="flex items-center gap-3 mb-4"><div className="w-12 h-12 rounded-full bg-slate-900 text-white flex items-center justify-center text-lg font-extrabold data-raw">{(user?.name || "?").slice(0, 1).toUpperCase()}</div>
          <div><div className="font-bold data-raw" data-testid="account-name-display">{user?.name}</div><div className="text-xs text-slate-500 data-raw">{user?.email}</div><RolePill role={user?.role} /></div></div>
        <div className="space-y-3">
          <div><Label className="text-xs">Nama</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="account-name" /></div>
          <div><Label className="text-xs">Username / Email login</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="account-email" /></div>
          <Button onClick={() => save({ name: f.name, email: f.email }, "Profil disimpan")} disabled={busy || !f.name || !f.email} className="w-full sm:w-auto bg-slate-900 text-white" data-testid="account-save-profile"><UserCog className="w-4 h-4 mr-1.5" />Simpan Profil</Button>
        </div>
      </Panel>
      <Panel title="Ganti Kata Sandi" testId="account-password">
        <div className="space-y-3">
          <div><Label className="text-xs">Kata sandi saat ini</Label><Input type="password" value={p.current_password} onChange={(e) => setP({ ...p, current_password: e.target.value })} data-testid="account-current-password" /></div>
          <div><Label className="text-xs">Kata sandi baru (min. 6)</Label><Input type="password" value={p.new_password} onChange={(e) => setP({ ...p, new_password: e.target.value })} data-testid="account-new-password" /></div>
          <div><Label className="text-xs">Ulangi kata sandi baru</Label><Input type="password" value={p.confirm} onChange={(e) => setP({ ...p, confirm: e.target.value })} data-testid="account-confirm-password" /></div>
          {p.confirm && p.confirm !== p.new_password && <div className="text-xs text-rose-600" data-testid="account-password-mismatch">Kata sandi baru tidak sama</div>}
          <Button onClick={() => save({ current_password: p.current_password, new_password: p.new_password }, "Kata sandi diganti")} disabled={busy || !p.current_password || p.new_password.length < 6 || p.new_password !== p.confirm} className="w-full sm:w-auto bg-slate-900 text-white" data-testid="account-save-password"><KeyRound className="w-4 h-4 mr-1.5" />Ganti Kata Sandi</Button>
        </div>
      </Panel>
      <Panel title="Tampilan" testId="account-appearance" className="lg:col-span-2">
        <div className="text-xs text-slate-500 mb-3 no-up">Pilih mode tampilan ala macOS. Pilihan tersimpan di perangkat ini dan berlaku untuk semua halaman.</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="theme-options">
          {[{ v: "light", l: "Terang", icon: Sun, bg: "bg-[#F5F5F7]", fg: "bg-white" },
            { v: "dark", l: "Gelap", icon: Moon, bg: "bg-[#1C1C1E]", fg: "bg-[#3A3A3C]" },
            { v: "system", l: "Otomatis (ikuti perangkat)", icon: Monitor, bg: "bg-gradient-to-r from-[#F5F5F7] to-[#1C1C1E]", fg: "bg-white/70" }].map((o) => {
            const Icon = o.icon; const on = theme === o.v;
            return <button key={o.v} onClick={() => setTheme(o.v)} className={`text-left rounded-2xl border-2 p-3 transition-colors ${on ? "border-[#0071E3]" : "border-transparent hover:border-[#D1D1D6]"}`} data-testid={`theme-${o.v}`} aria-pressed={on}>
              <div className={`${o.bg} rounded-xl h-16 p-2 flex flex-col gap-1.5 border border-black/5`}><div className={`${o.fg} rounded h-2 w-2/3`} /><div className={`${o.fg} rounded h-2 w-1/2`} /><div className={`${o.fg} rounded h-2 w-3/4`} /></div>
              <div className="flex items-center gap-2 mt-2 text-sm font-semibold"><Icon className="w-4 h-4" />{o.l}{on && <span className="ml-auto text-[11px] text-[#0071E3]">Aktif</span>}</div>
            </button>;
          })}
        </div>
      </Panel>
    </div>
  );
}

const EMPTY = { name: "", email: "", username: "", password: "", role: "ACCOUNTING" };

function UserDialog({ open, onOpenChange, initial, onSaved }) {
  const isEdit = !!initial?.id;
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF(initial?.id ? { name: initial.name, email: initial.email || "", username: initial.username || "", password: "", role: initial.role } : EMPTY); }, [initial, open]);
  const isPencatat = f.role === "PENCATAT";
  const save = async () => {
    setBusy(true);
    try {
      if (isEdit) {
        const body = { name: f.name, role: f.role };
        if (isPencatat) body.username = f.username; else body.email = f.email;
        if (f.password) body.password = f.password;
        await api.put(`/auth/users/${initial.id}`, body); toast.success("PENGGUNA DIPERBARUI");
      } else {
        const body = { name: f.name, password: f.password, role: f.role };
        if (isPencatat) body.username = f.username; else body.email = f.email;
        await api.post("/auth/users", body); toast.success("AKSES BARU DIBUAT");
      }
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const identFilled = isPencatat ? !!f.username?.trim() : !!f.email?.trim();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="user-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Edit Pengguna" : "Tambah Akses Baru"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Nama</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name" /></div>
          <div><Label className="text-xs">Peran</Label>
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(ROLE_INFO).map(([k, v]) => <SelectItem key={k} value={k}>{v.label} — {v.desc}</SelectItem>)}</SelectContent></Select></div>
          {isPencatat ? (
            <div><Label className="text-xs">Username login</Label><Input value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} placeholder="mis. dekwi" data-testid="user-username" /></div>
          ) : (
            <div><Label className="text-xs">Email login</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email" /></div>
          )}
          <div><Label className="text-xs">{isEdit ? "Kata sandi baru (kosongkan jika tidak diganti)" : "Kata sandi (min. 6)"}</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-password" /></div>
          <Button onClick={save} disabled={busy || !f.name || !identFilled || (!isEdit && f.password.length < 6) || (f.password && f.password.length < 6)} className="w-full bg-slate-900 text-white" data-testid="user-save">{isEdit ? "Simpan Perubahan" : "Buat Akses"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ManageUsers() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [dlg, setDlg] = useState({ open: false, initial: null });
  const load = () => api.get("/auth/users").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const del = async (u) => {
    if (!window.confirm(`Hapus akses ${u.email}? Tindakan ini tidak bisa dibatalkan.`)) return;
    try { await api.delete(`/auth/users/${u.id}`); toast.success("PENGGUNA DIHAPUS"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Panel title={`Kelola Pengguna (${rows.length})`} testId="users-panel" action={<Button size="sm" onClick={() => setDlg({ open: true, initial: null })} className="bg-slate-900 text-white" data-testid="user-add"><Plus className="w-4 h-4 mr-1" />Tambah Akses</Button>}>
      <div className="space-y-2">
        {rows.map((u) => <div key={u.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 border border-slate-200 rounded-lg p-3" data-testid={`user-row-${u.email || u.username}`}>
          <div className="flex-1 min-w-0"><div className="font-semibold data-raw truncate">{u.name} {u.id === user.id && <span className="text-[10px] text-slate-400">(SAYA)</span>}</div><div className="text-xs text-slate-500 data-raw truncate">{u.email || (u.username ? `@${u.username}` : "-")}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">Login terakhir {u.last_login_at ? new Date(u.last_login_at).toLocaleString("id-ID", { timeZone: "Asia/Makassar" }) : "-"}</div></div>
          <RolePill role={u.role} />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setDlg({ open: true, initial: u })} data-testid={`user-edit-${u.email || u.username}`}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
            <Button size="sm" variant="outline" disabled={u.id === user.id} onClick={() => del(u)} className="text-rose-600 border-rose-200" data-testid={`user-delete-${u.email || u.username}`}><Trash2 className="w-3.5 h-3.5 mr-1" />Hapus</Button>
          </div>
        </div>)}
      </div>
      <div className="mt-4 text-xs text-slate-500 space-y-0.5">{Object.entries(ROLE_INFO).map(([k, v]) => <div key={k}><b>{v.label}</b>: {v.desc}</div>)}</div>
      <UserDialog open={dlg.open} onOpenChange={(o) => setDlg((s) => ({ ...s, open: o }))} initial={dlg.initial} onSaved={load} />
    </Panel>
  );
}

export default function Account() {
  const { canManageUsers } = useAuth();
  return (
    <div>
      <PageTitle title="Akun Saya" desc="Pusat akun: ubah nama, email login, kata sandi, dan kelola akses pengguna." />
      <Tabs defaultValue="profile">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="profile" data-testid="account-tab-profile"><UserCog className="w-4 h-4 mr-1.5" />Profil</TabsTrigger>
          {canManageUsers && <TabsTrigger value="users" data-testid="account-tab-users"><Users className="w-4 h-4 mr-1.5" />Kelola Pengguna</TabsTrigger>}
        </TabsList>
        <TabsContent value="profile"><Profile /></TabsContent>
        {canManageUsers && <TabsContent value="users"><ManageUsers /></TabsContent>}
      </Tabs>
    </div>
  );
}
